import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { PointArray } from '@editor/interfaces/attribute'
import { CircleAnchorProps, PolygonPlaneMeshProps, PolylineProps } from '@editor/interfaces/canvas'
import { CanvasConfigV2, CanvasEventsExtra } from '@editor/interfaces/editor'
import { Polygon } from '@editor/interfaces/shape'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useConfigureCanvas from '@editor/pages/projects/blueprint-editor/hooks/useConfigureCanvas'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import { setMode } from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { getCameraProfile } from '@editor/services/Camera'
import { focusCameraToPlane } from '@editor/services/Editor'
import { findCenter, getCentroidOfPolygon3d, getRotationDirection, projectToScreen } from '@editor/services/Points'
import { getBoundingBoxFaces, sortRectanglePoints3D } from '@editor/services/Shape'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor } from '@editor/store/tool'
import { CircleAnchorIcon } from '@infraloop/common'
import { useSelector } from 'react-redux'
import { Line3, Plane, Raycaster, Vector2, Vector3 } from 'three'

import {
  SelectionStage,
  addPlanePoint,
  setPreviewPlanePoints,
  setSelectionStage,
  setWorkingCameraProfile,
  undoLastPlanePoint,
  updatePlanePoint,
} from '../store'
import useGridMesh from './useGridMesh'

const raycaster = new Raycaster()

let previousScreenPos: { x: number; y: number } | null = null
let is2FingerTouched = false
let isMouseDown = false

const ROTATION_SENSITIVITY = 0.0015

export default function useMainCanvas(): CanvasConfigV2 {
  // Context / Hooks
  const bpEditorContext = useBlueprintEditorContext()
  const { arcballControls, activeCamera, orthographicCamera, perspectiveCamera, sceneRef } = bpEditorContext
  const { getIntersection } = usePointSelection()
  const { addGridToCamera } = useGridMesh()
  const { orientOrthographicCamera, orientCamera, setupControls } = useConfigureCanvas(bpEditorContext)

  // Store
  const dispatch = useAppDispatch()
  const selectionStage = useSelector((state: RootState) => state.bpBlueprintCreator.selectionStage)
  const drawnPlanePoints = useSelector((state: RootState) => state.bpBlueprintCreator.drawnPlanePoints)
  const previewPlanePoints = useSelector((state: RootState) => state.bpBlueprintCreator.previewPlanePoints)
  const pcdBoundingBox = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingBox)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)
  const workingCameraProfile = useSelector((state: RootState) => state.bpBlueprintCreator.workingCameraProfile)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)

  // Refs
  const fineTuneTarget = useRef<Vector3>(new Vector3())
  const fineTuneDirection = useRef<Vector3>(new Vector3())
  const planeRef = useRef<Plane | null>(null)

  // State
  const [tempTarget, setTempTarget] = useState<Vector3>()
  const [movingAnchor, setMovingAnchor] = useState<number | undefined>()
  const [mouseAnchor, setMouseAnchor] = useState<PointArray | undefined>()

  // Derived
  const pcdBoundingBoxFaces = useMemo(
    () =>
      pcdBoundingBox ? getBoundingBoxFaces(new Vector3(...pcdBoundingBox[0]), new Vector3(...pcdBoundingBox[1])) : null,
    [pcdBoundingBox],
  )
  const isPreviousToolSelected = useMemo(
    () => prevSelectedTool === BlueprintEditorTools.BlueprintManager,
    [prevSelectedTool],
  )

  // Vars
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.BlueprintManager, [selectedTool])

  /**
   * Handle mouse up event.
   *
   * @param e MouseEvent
   * @param point Clicked point in 3D Space
   */
  const onMouseUp = useCallback(
    (me: React.MouseEvent<HTMLDivElement, MouseEvent>, { isDragged }: CanvasEventsExtra) => {
      if (!isToolSelected || (isDragged && movingAnchor === undefined)) return

      // Reset moving anchor state
      if (movingAnchor !== undefined) {
        dispatch(setCursor(CursorState.CROSSHAIR))
        setMovingAnchor(undefined)
        if (arcballControls.current) {
          arcballControls.current.enableRotate = true
          arcballControls.current.enableZoom = true
          arcballControls.current.enablePan = true
        }
        return
      }

      const point = getIntersection(me)
      if (!point) return

      // ## Draw stage only
      if (selectionStage === SelectionStage.DRAW) {
        if (drawnPlanePoints.length < 3) {
          dispatch(addPlanePoint({ point }))
        }

        // We need 3 but uses 2 here since the third point is added on the current mouse up.
        if (drawnPlanePoints.length === 2) {
          dispatch(setSelectionStage(SelectionStage.ROTATE))
          setMouseAnchor(undefined)
        }
      }
    },
    [isToolSelected, drawnPlanePoints, selectionStage, movingAnchor, arcballControls, dispatch, getIntersection],
  )

  /**
   * Handle mouse down event.
   */
  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isToolSelected) return
      isMouseDown = true

      if (selectionStage === SelectionStage.FOCUS) {
        dispatch(setCursor(CursorState.GRABBING))
        is2FingerTouched = e.button === 2
        previousScreenPos = { x: e.clientX, y: e.clientY }
      }
    },
    [isToolSelected, selectionStage, dispatch],
  )

  /**
   * Fine tune positioning of the camera.
   * Rotate camera around the target while being parallel on the plane.
   */
  const onFocusFineTune = useCallback(
    (me: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const screenPos = { x: me.clientX, y: me.clientY }
      const canvasBox = document.getElementById('canvasBox') as HTMLElement

      if (!arcballControls.current || !previousScreenPos || !canvasBox || !planeRef.current) {
        return
      }

      // Define some stuff for later
      const camera = orthographicCamera.current
      camera.getWorldDirection(fineTuneDirection.current)

      // Get target by raycasting camera onto plane
      raycaster.ray.set(camera.position, fineTuneDirection.current)
      const found = raycaster.ray.intersectPlane(planeRef.current, fineTuneTarget.current)
      if (!found) return

      // ## Do panning
      if (is2FingerTouched) {
        setTempTarget(found)
        return
      }

      // ## Otherwise, do rotation

      // Get target in screen space
      const canvasRect = canvasBox.getBoundingClientRect()
      const targetScreen = projectToScreen(fineTuneTarget.current, camera, canvasRect.width, canvasRect.height)

      // if mouse is higher than the target, rotate in the opposite direction
      // also if mouse is on the left side of the target, rotate in the opposite direction
      // Negative because it's the opposite of what three-stdlib needs.
      const sign = -getRotationDirection(
        new Vector2(targetScreen.x, targetScreen.y),
        new Vector2(previousScreenPos.x, previousScreenPos.y),
        new Vector2(screenPos.x, screenPos.y),
      )

      // calculate total distance travelled by mouse
      const delta =
        new Vector2(screenPos.x, screenPos.y).distanceTo(new Vector2(previousScreenPos.x, previousScreenPos.y)) * sign

      // // Calculate the rotation angle based on delta movement and sensitivity
      const angle = delta * ROTATION_SENSITIVITY

      camera.rotateZ(angle)
      camera.updateMatrixWorld()

      previousScreenPos = { x: screenPos.x, y: screenPos.y }
    },
    [orthographicCamera, arcballControls, planeRef],
  )

  /**
   * Undo the last action.
   * 1. When drawing, go back a point.
   * 2. If completed, re-open (from ROTATE to DRAW stage).
   * 3. If on FOCUS stage, go back to ROTATE stage.
   */
  const onUndo = useCallback(() => {
    if (!isToolSelected) return

    setMouseAnchor(undefined)

    if (selectionStage === SelectionStage.DRAW) {
      dispatch(undoLastPlanePoint())
    } else if (selectionStage === SelectionStage.ROTATE) {
      dispatch(undoLastPlanePoint())
      dispatch(setSelectionStage(SelectionStage.DRAW))
      dispatch(setPreviewPlanePoints([]))
      planeRef.current = null
    } else if (selectionStage === SelectionStage.FOCUS) {
      dispatch(setSelectionStage(SelectionStage.ROTATE))
      dispatch(setCursor(CursorState.CROSSHAIR))

      if (arcballControls.current) arcballControls.current.enableRotate = true

      // If user is on 2D mode, switch back to 3D mode.
      if (mode === Mode.Editor2D) {
        dispatch(setMode(Mode.Editor3D))
        setupControls(Mode.Editor3D)
        orientCamera(Mode.Editor3D)
      }
    }
  }, [isToolSelected, selectionStage, arcballControls, mode, orientCamera, setupControls, dispatch])

  /**
   * Handle moving of the anchor.
   */
  const onMoveAnchor = useCallback(
    (point: PointArray) => {
      if (!isToolSelected || movingAnchor === undefined) return

      dispatch(
        updatePlanePoint({
          index: movingAnchor,
          point,
        }),
      )
    },
    [isToolSelected, movingAnchor, dispatch],
  )

  /**
   * Once we have enough points, calculate the plane.
   */
  useEffect(() => {
    if (
      drawnPlanePoints.length < 3 ||
      selectionStage === SelectionStage.DRAW ||
      !pcdBoundingBox ||
      !pcdBoundingBoxFaces
    )
      return

    // make plane and find out plane that extends the entire PCD bounding box
    const vectorPoints = drawnPlanePoints.map((p) => new Vector3(...p))
    const pln = new Plane().setFromCoplanarPoints(vectorPoints[0], vectorPoints[1], vectorPoints[2])
    const center = findCenter(vectorPoints) || vectorPoints[0]

    // make sure the normal is pointing towards the camera
    const cameraDirection = new Vector3()
    perspectiveCamera.current.getWorldDirection(cameraDirection)
    if (pln.normal.angleTo(cameraDirection) < Math.PI / 2) {
      pln.normal.negate()
      pln.constant = -pln.normal.dot(center)
    }

    planeRef.current = pln

    // find the edges that intersect with the plane
    const intersectedPoint: Vector3[] = []
    for (let i = 0; i < pcdBoundingBoxFaces.length; i += 1) {
      const points = pcdBoundingBoxFaces[i]

      for (let j = 0; j < points.length; j += 1) {
        const p1 = points[j]
        const p2 = points[(j + 1) % points.length]
        const line = new Line3(p1, p2)

        if (pln.intersectsLine(line)) {
          const intersect = new Vector3()
          pln.intersectLine(line, intersect)
          intersectedPoint.push(intersect)

          // only need 4 points
          if (intersectedPoint.length === 4) break
        }
      }

      // only need 4 points
      if (intersectedPoint.length === 4) break
    }

    if (intersectedPoint.length >= 4) {
      dispatch(setPreviewPlanePoints(sortRectanglePoints3D(intersectedPoint).map((p) => p.toArray())))
    }
  }, [
    drawnPlanePoints,
    pcdBoundingBoxFaces,
    selectionStage,
    pcdBoundingBox,
    movingAnchor,
    perspectiveCamera,
    sceneRef,
    dispatch,
  ])

  /**
   * Once we enter FOCUS stage, set focus to the target.
   */
  useEffect(() => {
    if (
      selectionStage !== SelectionStage.FOCUS ||
      !perspectiveCamera.current ||
      !arcballControls.current ||
      !drawnPlanePoints.length ||
      !isCreatingBlueprint
    )
      return

    // redo plane
    const vectorPoints = drawnPlanePoints.map((p) => new Vector3(...p))
    const pln = new Plane().setFromCoplanarPoints(vectorPoints[0], vectorPoints[1], vectorPoints[2])
    const center = findCenter(vectorPoints) || vectorPoints[0]

    // make sure the normal is pointing towards the camera
    const cameraDirection = new Vector3()
    perspectiveCamera.current.getWorldDirection(cameraDirection)
    if (pln.normal.angleTo(cameraDirection) < Math.PI / 2) {
      pln.normal.negate()
      pln.constant = -pln.normal.dot(center)
    }

    // Calculate target point, should be center of what was drawn
    const tgt = getCentroidOfPolygon3d(drawnPlanePoints.map((p) => new Vector3(...p))).toArray()

    // Step 1: Extract the old up vector from the original quaternion
    const oldUp = perspectiveCamera.current.up.clone()

    // Step 2: Calculate the new right vector as the cross-product of forward and old up
    const right = new Vector3().crossVectors(pln.normal, oldUp).normalize()

    focusCameraToPlane(
      arcballControls.current,
      perspectiveCamera.current,
      tgt,
      pln.normal,
      right,
      undefined,
      perspectiveCamera.current.position.distanceTo(new Vector3(...tgt)),
    )

    setTimeout(() => {
      // redo plane
      pln.setFromCoplanarPoints(vectorPoints[0], vectorPoints[1], vectorPoints[2])
      const center2 = findCenter(vectorPoints) || vectorPoints[0]

      // make sure the normal is pointing towards the camera
      perspectiveCamera.current.getWorldDirection(cameraDirection)
      if (pln.normal.angleTo(cameraDirection) < Math.PI / 2) {
        pln.normal.negate()
        pln.constant = -pln.normal.dot(center2)
      }

      planeRef.current = pln

      dispatch(
        setWorkingCameraProfile(getCameraProfile(perspectiveCamera.current, arcballControls.current!) || undefined),
      )
    }) // slight delay to give time for camera to settle
  }, [isCreatingBlueprint, selectionStage, drawnPlanePoints, perspectiveCamera, arcballControls, dispatch])

  useEffect(() => {
    if (!workingCameraProfile || !isCreatingBlueprint || !isToolSelected || selectionStage !== SelectionStage.FOCUS)
      return

    dispatch(setMode(Mode.Editor2D))
    setupControls(Mode.Editor2D)
    orientOrthographicCamera(workingCameraProfile)

    addGridToCamera(activeCamera.current, workingCameraProfile)
  }, [
    selectionStage,
    isToolSelected,
    prevSelectedTool,
    workingCameraProfile,
    activeCamera,
    isCreatingBlueprint,
    addGridToCamera,
    setupControls,
    orientOrthographicCamera,
    dispatch,
  ])

  /**
   * Handle mouse up event on document level.
   * Mainly used by FOCUS stage to detect mouse up outside of canvas.
   */
  useEffect(() => {
    const mouseUp = () => {
      previousScreenPos = null
      isMouseDown = false

      // If we're on FOCUS stage, mouse up can mean done with dragging, so change cursor back to GRAB.
      if (selectionStage === SelectionStage.FOCUS) {
        dispatch(setCursor(CursorState.GRAB))
        setMovingAnchor(undefined)
      }
    }

    document.addEventListener('mouseup', mouseUp)

    return () => {
      document.removeEventListener('mouseup', mouseUp)
    }
  }, [selectionStage, dispatch])

  /**
   * Cleanup working state when tool is deselected.
   */
  useEffect(() => {
    if (!isToolSelected && isPreviousToolSelected && !isCreatingBlueprint) {
      planeRef.current = null
      setMovingAnchor(undefined)
      setMouseAnchor(undefined)
    }
  }, [
    isToolSelected,
    isPreviousToolSelected,
    isCreatingBlueprint,
    selectedTool,
    prevSelectedTool,
    arcballControls,
    dispatch,
  ])

  /**
   * Drawn plane distance labels.
   * Also holds interaction events for moving the anchor.
   */
  const drawnPlaneLines = useMemo((): PolylineProps[] => {
    if (!drawnPlanePoints.length) return []

    return [
      {
        id: `camera-focus-plane-points`,
        points: [...drawnPlanePoints, drawnPlanePoints[0]], // Close the polygon
        hideAnchors: true,
        anchorProps: {
          color: '#248CE4',
          scale: 1.3,
        },
      },
    ]
  }, [drawnPlanePoints])

  const placedAnchors = useMemo((): CircleAnchorProps[] => {
    if (!drawnPlanePoints.length) return []
    const isDraggable = [SelectionStage.DRAW, SelectionStage.ROTATE].includes(selectionStage)

    return drawnPlanePoints.map((point, index) => ({
      id: `camera-focus-plane-anchor-${crypto.randomUUID()}`,
      point,
      color: '#248CE4',
      tooltipText: isDraggable ? '点を移動' : undefined,
      scale: 1.3,
      icon: isDraggable ? CircleAnchorIcon.MOVE : undefined,
      iconScale: 1.1,
      iconColor: '#ffffff',
      outlineOpacity: isDraggable ? 0 : 1,
      /**
       * When user holds down the anchor, initiate moving of the anchor.
       * The reset of this state is done in onMouseUp().
       */
      onDown: !isDraggable
        ? undefined
        : () => {
            dispatch(setCursor(CursorState.GRABBING))

            if (arcballControls.current) {
              arcballControls.current.enableRotate = false
              arcballControls.current.enableZoom = false
              arcballControls.current.enablePan = false
            }
            setMovingAnchor(index)
          },
      onEnter: !isDraggable
        ? undefined
        : () => {
            dispatch(setCursor(CursorState.GRAB))
          },
      onLeave: !isDraggable
        ? undefined
        : () => {
            dispatch(setCursor(CursorState.CROSSHAIR))
          },
    }))
  }, [drawnPlanePoints, selectionStage, arcballControls, dispatch])

  /**
   * Plane mesh for preview.
   */
  const previewPlaneMesh = useMemo((): PolygonPlaneMeshProps[] => {
    if (previewPlanePoints.length < 4 || selectionStage === SelectionStage.DRAW || !isToolSelected) return []

    return [
      {
        id: 'camara-profile-preview-plane',
        polygon: {
          shape_id: 'camara-profile-preview-plane',
          positions: previewPlanePoints,
          center: getCentroidOfPolygon3d(previewPlanePoints.map((p) => new Vector3(...p))).toArray(),
          vertices: previewPlanePoints.map((p) => [p[0], p[1]]),
          transformation: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        } as Polygon,
        opacity: selectionStage === SelectionStage.FOCUS || movingAnchor !== undefined ? 0.25 : 0.5,
      },
    ]
  }, [previewPlanePoints, selectionStage, movingAnchor, isToolSelected])

  return {
    objects: {
      polylines: [SelectionStage.FOCUS, SelectionStage.SAVING].includes(selectionStage) ? [] : [...drawnPlaneLines],
      circleAnchors: isToolSelected
        ? [
            ...placedAnchors,
            ...(mouseAnchor
              ? [
                  {
                    id: 'camera-focus-mouse-anchor',
                    point: mouseAnchor,
                    color: 'red',
                  },
                ]
              : []),
          ]
        : [],
      polygonPlaneMeshes: [...previewPlaneMesh],
    },
    events: {
      onMouseUp,
      onMouseDown,
      onUndo,
      // onTouchStart: (ev, point) => {
      //   if (!isToolSelected) return
      //   is2FingerTouched = ev.touches.length > 1
      //   setLastTouchPoint(point)
      //   setOnDownData({
      //     position: [ev.touches[0].clientX, ev.touches[0].clientY],
      //     timestamp: Date.now(),
      //   })
      //   previousScreenPos = { x: ev.touches[0].clientX, y: ev.touches[0].clientY }
      // },
      // onTouchEnd: (ev) => {
      //   if (!isToolSelected) return
      //   onMouseUp(undefined, ev, lastTouchPoint)
      // },
      onMouseMove: useCallback(
        (me: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          if (!isToolSelected) return

          // Fine-tuning position of the camera
          if (selectionStage === SelectionStage.FOCUS && previousScreenPos && isMouseDown) {
            onFocusFineTune(me)
            return
          }

          // Anything else, it needs a point in 3D space.
          const point = getIntersection(me)
          if (!point) return

          // Moving of an anchor
          if (movingAnchor !== undefined) {
            onMoveAnchor(point)
            return
          }

          // update constantly following anchor, only during drawing stage
          if (selectionStage === SelectionStage.DRAW) {
            setMouseAnchor(point)
          }
        },
        [isToolSelected, selectionStage, movingAnchor, getIntersection, onMoveAnchor, onFocusFineTune],
      ),
    },
  }
}
